import { ApolloError } from '@apollo/client';

import {
  SALE_ORDER_ITEM_KIND_FROM_GQL,
  SALE_ORDER_STATUS_FROM_GQL,
} from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';

/**
 * Shared sale-order shapes, snapshot/label helpers, the 80mm ticket renderer, and the
 * GraphQL error → i18n-key mapper. Extracted out of `CashierPosPage.tsx` so
 * `single-station/SingleStationPosPage.tsx` can reuse the exact same ticket output and
 * error messages instead of duplicating them (rule §13 — tickets read stored results,
 * they never recompute; a second copy would risk silently drifting from this one).
 */

export interface OrderCustomerSnapshot {
  first_name?: string;
  last_name?: string | null;
  phone?: string;
}

export interface OrderPetSnapshot {
  name?: string;
  species?: string | null;
}

export interface KitBreakdownLine {
  item_kind: string;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  total_cents: string;
}

export interface KitBreakdownLookupTarget {
  key: string;
  kind: 'id' | 'code';
  value: string;
}

export interface TicketPaymentMethod {
  payment_method_id: string;
  payment_method_code: string;
  payment_method_name: string;
  amount_cents: string;
  reference_number: string | null;
}

export interface SaleOrderItem {
  id: string;
  line_no: number;
  item_kind: string;
  from_kit_component?: boolean;
  kit_id?: string | null;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  subtotal_cents: string;
  discount_cents: string;
  total_cents: string;
  kit_breakdown_lines?: KitBreakdownLine[];
}

export interface SaleOrder {
  id: string;
  folio_number: string | null;
  status: string;
  appointment_id: string | null;
  branch_id: string;
  consulting_room_id: string | null;
  consulting_room_name?: string | null;
  customer_id: string | null;
  pet_id: string | null;
  veterinarian_id: string | null;
  customer_snapshot_json?: OrderCustomerSnapshot | string | null;
  pet_snapshot_json?: OrderPetSnapshot | string | null;
  cashier_initial_notes?: string | null;
  current_weight_grams?: number | null;
  subtotal_cents: string;
  discount_cents: string;
  tax_cents: string;
  total_cents: string;
  paid_cents: string;
  pending_cents: string;
  sent_to_cashier_at: string | null;
  created_at: string;
  paid_at: string | null;
  return_reason: string | null;
  clinical_notes: string | null;
  clinical_visit_id?: string | null;
  items: SaleOrderItem[];
}

const TICKET_LOGO_PATH = '/totem_logo_ticket.png';
const TICKET_DEFAULT_COPIES = 2;
let cached_ticket_logo_data_uri: string | null | undefined;

export function parseSnapshot<T>(value: T | string | null | undefined): T | null {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function customerLabel(snap: OrderCustomerSnapshot | string | null | undefined): string {
  if (typeof snap === 'string') return customerLabel(parseSnapshot<OrderCustomerSnapshot>(snap));
  if (!snap) return '-';
  const first = snap.first_name ?? '';
  const last = snap.last_name ?? '';
  const name = [first, last].filter(Boolean).join(' ').trim();
  if (name) return name;
  return snap.phone ?? '-';
}

export function petLabel(snap: OrderPetSnapshot | string | null | undefined): string {
  if (typeof snap === 'string') return petLabel(parseSnapshot<OrderPetSnapshot>(snap));
  if (!snap?.name) return '-';
  return snap.species ? `${snap.name} - ${snap.species}` : snap.name;
}

export function normalizeSaleOrder(order: SaleOrder): SaleOrder {
  return {
    ...order,
    customer_snapshot_json: parseSnapshot<OrderCustomerSnapshot>(order.customer_snapshot_json),
    pet_snapshot_json: parseSnapshot<OrderPetSnapshot>(order.pet_snapshot_json),
    status:
      SALE_ORDER_STATUS_FROM_GQL[order.status as keyof typeof SALE_ORDER_STATUS_FROM_GQL] ??
      order.status,
    items: order.items.map((item) => ({
      ...item,
      item_kind:
        SALE_ORDER_ITEM_KIND_FROM_GQL[
          item.item_kind as keyof typeof SALE_ORDER_ITEM_KIND_FROM_GQL
        ] ?? item.item_kind,
    })),
  };
}

export function timeLabel(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function dateTimeLabel(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export function ticketLabel(ticket_folio: string | null, sale_folio: string | null): string {
  if (ticket_folio) return `T-${ticket_folio}`;
  if (sale_folio) return `V-${sale_folio}`;
  return '-';
}

export interface PrintSaleTicketOptions {
  copies?: number;
  mode: 'ticket' | 'reprint';
  cashier_name: string;
  ticket_number?: string | null;
  kit_breakdowns?: Record<string, string[]>;
  payment_methods?: TicketPaymentMethod[];
  payment_method_summary?: string | null;
  open_account_kind?: 'normal' | 'hospitalization_1' | 'hospitalization_2' | null;
  open_account_pending_cents?: string | null;
  open_account_credit_cents?: string | null;
}

export async function printSaleTicket(
  order: SaleOrder,
  options: PrintSaleTicketOptions,
  target_window?: Window | null,
): Promise<void> {
  if (typeof window === 'undefined') return;
  const normalized_options = { ...options, copies: options.copies ?? TICKET_DEFAULT_COPIES };
  const logo_data_uri = await resolveTicketLogoDataUri();
  const printable = buildTicketHtml(order, normalized_options, logo_data_uri);
  const popup = target_window ?? window.open('', '_blank', 'width=420,height=720');
  if (!popup) {
    const current = document.body.innerHTML;
    document.body.innerHTML = printable;
    window.print();
    document.body.innerHTML = current;
    window.location.reload();
    return;
  }
  popup.document.open();
  popup.document.write(printable);
  popup.document.close();
  await waitForPopupReady(popup);
  await waitForPopupImages(popup);
  popup.addEventListener(
    'afterprint',
    () => {
      popup.close();
    },
    { once: true },
  );
  popup.focus();
  popup.print();
}

function buildTicketHtml(
  order: SaleOrder,
  options: {
    copies: number;
    mode: 'ticket' | 'reprint';
    cashier_name: string;
    ticket_number?: string | null;
    kit_breakdowns?: Record<string, string[]>;
    payment_methods?: TicketPaymentMethod[];
    payment_method_summary?: string | null;
    open_account_kind?: 'normal' | 'hospitalization_1' | 'hospitalization_2' | null;
    open_account_pending_cents?: string | null;
    open_account_credit_cents?: string | null;
  },
  logo_data_uri: string | null,
): string {
  const now_label = escapeHtml(dateTimeLabel(new Date().toISOString()));
  const normalized_ticket_number = options.ticket_number?.trim();
  const ticket_number = escapeHtml(
    normalized_ticket_number && normalized_ticket_number.length > 0
      ? normalized_ticket_number
      : ticketLabel(null, order.folio_number ?? order.id.slice(0, 8)),
  );
  const cashier = escapeHtml(options.cashier_name);
  const ticket_kind = options.mode === 'reprint' ? 'REIMPRESION' : 'TICKET';
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${escapeHtml(logo_data_uri)}" alt="Totem" />`
    : '<p class="logo-fallback">TOTEM</p>';
  const tutor_name = escapeHtml(customerLabel(order.customer_snapshot_json));
  const pet_name = escapeHtml(petLabel(order.pet_snapshot_json));
  const open_account_pending_cents = safeTicketBigInt(options.open_account_pending_cents ?? null);
  const open_account_credit_cents = safeTicketBigInt(options.open_account_credit_cents ?? null);
  const payment_block_markup = buildTicketPaymentMarkup(order, options.payment_methods ?? [], {
    payment_method_summary: options.payment_method_summary ?? null,
  });
  const rows = groupItemsByKit(order.items)
    .map((group) => {
      const header = group.kit_label
        ? `<tr><td colspan="3" class="group">${escapeHtml(group.kit_label)}</td></tr>`
        : '';
      const body = group.items
        .map((item) => {
          const breakdown = kitBreakdownDisplayRows(item, options.kit_breakdowns);
          const breakdown_rows =
            item.item_kind === 'kit' && breakdown.length > 0
              ? breakdown
                  .map(
                    (entry) => `
        <tr class="kit-sub">
          <td>  - ${escapeHtml(entry)}</td>
          <td class="num"></td>
          <td class="num"></td>
        </tr>`,
                  )
                  .join('')
              : '';
          return `
        <tr>
          <td>${escapeHtml(group.kit_label ? kitComponentLabel(item.item_name) : item.item_name)}</td>
          <td class="num">${escapeHtml(item.quantity)}</td>
          <td class="num">${escapeHtml(formatCentsString(item.total_cents))}</td>
        </tr>${breakdown_rows}`;
        })
        .join('');
      return `${header}${body}`;
    })
    .join('');
  const copy = `
    <section class="ticket">
      <p class="kind">${ticket_kind}</p>
      <div class="logo-wrap">
        ${logo_markup}
      </div>
      <p class="brand">Hospital Veterinario de Especialidades Totem</p>
      <p class="line">Direccion: Prolongacion Cuauhtemoc 224. Huizache 1, Durango, Mexico</p>
      <p class="line">Telefono: 618 112 3896</p>
      <p class="line">Fecha: ${now_label}</p>
      <p class="line">No de Ticket: ${ticket_number}</p>
      <p class="line">Le Atendio: ${cashier}</p>
      <hr />
      <p class="line"><strong>Tutor:</strong> ${tutor_name}</p>
      <p class="line"><strong>Mascota:</strong> ${pet_name}</p>
      <hr />
      <table>
        <thead><tr><th>Concepto</th><th class="num">Cant.</th><th class="num">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <hr />
      <p class="line"><strong>Subtotal:</strong> ${escapeHtml(formatCentsString(order.subtotal_cents))}</p>
      <p class="line"><strong>Concepto descuento:</strong> -${escapeHtml(
        formatCentsString(order.discount_cents),
      )}</p>
      <p class="line"><strong>IVA:</strong> ${escapeHtml(formatCentsString(order.tax_cents))}</p>
      <p class="total">Total ${escapeHtml(formatCentsString(order.total_cents))}</p>
      ${payment_block_markup}
      ${
        open_account_pending_cents > 0n || open_account_credit_cents > 0n
          ? `
        <div class="payment-block">
          <p class="line"><strong>Cuenta abierta:</strong> ${escapeHtml(
            options.open_account_kind === 'hospitalization_1'
              ? 'Hospitalización 1'
              : options.open_account_kind === 'hospitalization_2'
                ? 'Hospitalización 2'
                : 'Normal',
          )}</p>
          ${
            open_account_pending_cents > 0n
              ? `<p class="line"><strong>Pendiente:</strong> ${escapeHtml(
                  formatCentsString(open_account_pending_cents.toString()),
                )}</p>`
              : ''
          }
          ${
            open_account_credit_cents > 0n
              ? `<p class="line"><strong>Anticipo:</strong> ${escapeHtml(
                  formatCentsString(open_account_credit_cents.toString()),
                )}</p>`
              : ''
          }
        </div>`
          : ''
      }
      <div class="footer">
        <p class="footer-main">CONSERVE ESTE COMPROBANTE</p>
        <p class="footer-sub">ES INDISPENSABLE PARA CUALQUIER ACLARACION</p>
        <p class="footer-thanks">GRACIAS POR TU VISITA!</p>
        <p class="footer-sub">NO OLVIDES SEGUIRNOS EN NUESTRAS REDES SOCIALES</p>
        <p class="footer-social">@hospital.totem</p>
        <p class="footer-main">DUDAS O ACLARACIONES:</p>
        <p class="footer-contact">618 112 3896</p>
      </div>
    </section>`;
  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Ticket ${ticket_number}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          html, body { margin: 0; width: 80mm; }
          body { font-family: Arial, Helvetica, sans-serif; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .ticket { width: 80mm; box-sizing: border-box; padding: 2.2mm 2.2mm 2mm; page-break-after: always; break-after: page; }
          .ticket:last-child { page-break-after: auto; break-after: auto; }
          p { margin: 2px 0; font-size: 11px; line-height: 1.2; }
          .kind { margin: 0 0 4px; text-align: center; font-size: 12px; font-weight: 800; letter-spacing: 0.4px; }
          .logo-wrap { text-align: center; margin: 0 0 4px; }
          .logo { width: 52mm; max-width: 100%; height: auto; }
          .logo-fallback { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: 0.8px; }
          .brand { text-align: center; font-weight: 700; font-size: 11px; margin-bottom: 3px; }
          .line { margin: 1px 0; }
          .payment-block { margin-top: 4px; }
          .payment-row { margin-top: 3px; }
          .payment-line { margin-top: 0; }
          .payment-ref { margin: 0 0 0 6px; font-size: 9px; line-height: 1.2; }
          table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 10px; }
          th { text-align: left; border-bottom: 1px solid #111; padding-bottom: 2px; }
          td { padding: 2px 0; vertical-align: top; }
          .group { font-weight: 700; padding-top: 8px; }
          .kit-sub td { font-size: 9px; color: #555; }
          .num { text-align: right; white-space: nowrap; }
          .total { font-size: 13px; font-weight: 800; text-align: right; margin-top: 4px; }
          .footer { text-align: center; margin-top: 8px; border-top: 1px solid #111; padding-top: 6px; }
          .footer-main { font-weight: 800; font-size: 11px; margin: 2px 0; }
          .footer-sub { font-size: 9px; margin: 2px 0; }
          .footer-thanks { font-weight: 900; font-size: 18px; line-height: 1; margin: 4px 0; }
          .footer-social { font-weight: 700; font-size: 14px; line-height: 1; margin: 3px 0; }
          .footer-contact { font-weight: 700; font-size: 15px; line-height: 1; margin: 3px 0; }
          hr { border: 0; border-top: 1px solid #111; margin: 5px 0; }
          @media print {
            html, body { width: 80mm; }
            .ticket { page-break-after: always; break-after: page; }
            .ticket:last-child { page-break-after: auto; break-after: auto; }
          }
        </style>
      </head>
      <body>${Array.from({ length: options.copies }, () => copy).join('')}</body>
    </html>`;
}

function buildTicketPaymentMarkup(
  order: SaleOrder,
  payment_methods: readonly TicketPaymentMethod[],
  options: { payment_method_summary: string | null },
): string {
  const paid_cents = safeTicketBigInt(order.paid_cents);
  const pending_cents = safeTicketBigInt(order.pending_cents);
  const has_open_account_pending =
    pending_cents > 0n && (order.status === 'partially_paid' || order.status === 'open_account');

  if (payment_methods.length > 0) {
    const summary = summarizePaymentMethodLines(payment_methods) ?? options.payment_method_summary;
    const payment_rows = payment_methods
      .map((payment) => {
        const reference_line = payment.reference_number?.trim()
          ? `<p class="payment-ref">Referencia: ${escapeHtml(payment.reference_number.trim())}</p>`
          : '';
        return `
        <div class="payment-row">
          <p class="line payment-line"><strong>${escapeHtml(payment.payment_method_name)}:</strong> ${escapeHtml(formatCentsString(payment.amount_cents))}</p>
          ${reference_line}
        </div>`;
      })
      .join('');
    const pending_markup = has_open_account_pending
      ? `
        <p class="line"><strong>Pago parcial en caja:</strong> ${escapeHtml(
          formatCentsString(paid_cents.toString()),
        )}</p>
        <p class="line"><strong>Pendiente en cuenta abierta:</strong> ${escapeHtml(
          formatCentsString(pending_cents.toString()),
        )}</p>
        <p class="line"><strong>Estatus:</strong> Cuenta abierta con saldo pendiente</p>`
      : '';
    return `
      <div class="payment-block">
        <p class="line"><strong>Pago:</strong> ${escapeHtml(summary ?? 'Registrado')}</p>
        ${payment_rows}
        ${pending_markup}
      </div>`;
  }

  if (has_open_account_pending) {
    return `
      <div class="payment-block">
        <p class="line"><strong>Pago parcial en caja:</strong> ${escapeHtml(
          formatCentsString(paid_cents.toString()),
        )}</p>
        <p class="line"><strong>Pendiente en cuenta abierta:</strong> ${escapeHtml(
          formatCentsString(pending_cents.toString()),
        )}</p>
        <p class="line"><strong>Estatus:</strong> Cuenta abierta con saldo pendiente</p>
      </div>`;
  }

  return `
    <div class="payment-block">
      <p class="line"><strong>Pagado:</strong> ${escapeHtml(formatCentsString(paid_cents.toString()))}</p>
    </div>`;
}

export function summarizePaymentMethodLines(
  methods: readonly {
    method_name?: string | null;
    payment_method_name?: string | null;
  }[],
): string | null {
  const names = methods
    .map((method) => (method.method_name ?? method.payment_method_name ?? '').trim())
    .filter((name) => name.length > 0);
  if (names.length === 0) return null;
  const unique_names = new Set(names);
  if (unique_names.size === 1) return names[0] ?? null;
  return 'Pago mixto';
}

export function safeTicketBigInt(value: string | null | undefined): bigint {
  try {
    if (!value || !/^-?\d+$/.test(value)) return 0n;
    return BigInt(value);
  } catch {
    return 0n;
  }
}

async function resolveTicketLogoDataUri(): Promise<string | null> {
  if (cached_ticket_logo_data_uri !== undefined) return cached_ticket_logo_data_uri;
  try {
    const response = await fetch(TICKET_LOGO_PATH, { cache: 'force-cache' });
    if (!response.ok) {
      cached_ticket_logo_data_uri = null;
      return null;
    }
    const blob = await response.blob();
    cached_ticket_logo_data_uri = await blobToDataUri(blob);
    return cached_ticket_logo_data_uri;
  } catch {
    cached_ticket_logo_data_uri = null;
    return null;
  }
}

export function parseWeightKgToGrams(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(',', '.');
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error('weight_invalid');
  }
  const grams = Math.round(value * 1000);
  if (!Number.isInteger(grams) || grams <= 0) {
    throw new Error('weight_invalid');
  }
  return grams;
}

export function normalizeOptionalText(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function adjustQuantityByUnit(current_qty: string, delta: -1 | 1): string {
  const current = Number(current_qty);
  if (!Number.isFinite(current)) return '1';
  const next = delta === 1 ? current + 1 : Math.max(1, current - 1);
  return String(next);
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('logo_read_failed'));
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('logo_read_failed'));
    };
    reader.readAsDataURL(blob);
  });
}

async function waitForPopupReady(popup: Window): Promise<void> {
  if (popup.document.readyState === 'complete') return;
  await Promise.race([
    new Promise<void>((resolve) => popup.addEventListener('load', () => resolve(), { once: true })),
    delay(1200),
  ]);
}

async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getKitBreakdownKey(item: SaleOrderItem): string | null {
  if (item.item_kind !== 'kit') return null;
  if (item.kit_id) return `id:${item.kit_id}`;
  const code = item.item_code.trim();
  return code.length > 0 ? `code:${code}` : null;
}

export function collectKitBreakdownTargets(
  items: readonly SaleOrderItem[],
): KitBreakdownLookupTarget[] {
  const map = new Map<string, KitBreakdownLookupTarget>();
  for (const item of items) {
    if (item.item_kind !== 'kit') continue;
    if (item.kit_breakdown_lines && item.kit_breakdown_lines.length > 0) continue;
    if (item.kit_id) {
      map.set(`id:${item.kit_id}`, { key: `id:${item.kit_id}`, kind: 'id', value: item.kit_id });
      continue;
    }
    const code = item.item_code.trim();
    if (code.length === 0) continue;
    map.set(`code:${code}`, { key: `code:${code}`, kind: 'code', value: code });
  }
  return Array.from(map.values());
}

export function kitBreakdownDisplayRows(
  item: SaleOrderItem,
  fallback_by_key?: Record<string, string[]>,
): string[] {
  if (item.item_kind !== 'kit') return [];
  if (item.kit_breakdown_lines && item.kit_breakdown_lines.length > 0) {
    return item.kit_breakdown_lines.map(
      (line) =>
        `${line.item_code} - ${line.item_name} ${line.quantity} x ${formatCentsString(
          line.unit_price_cents,
        )} = ${formatCentsString(line.total_cents)}`,
    );
  }
  const key = getKitBreakdownKey(item);
  const fallback = key ? (fallback_by_key?.[key] ?? []) : [];
  return fallback.length > 0 ? fallback : ['Sin componentes configurados para este kit'];
}

function extractKitLabel(item_name: string): string | null {
  const idx = item_name.indexOf(' - ');
  if (idx <= 0) return null;
  return item_name.slice(0, idx).trim() || null;
}

export function kitComponentLabel(item_name: string): string {
  const idx = item_name.indexOf(' - ');
  if (idx <= 0) return item_name;
  return item_name.slice(idx + 3).trim() || item_name;
}

export function groupItemsByKit(
  items: readonly SaleOrderItem[],
): { kit_label: string | null; items: SaleOrderItem[] }[] {
  const groups: { kit_label: string | null; items: SaleOrderItem[] }[] = [];
  for (const item of items) {
    const looks_like_kit_component =
      item.from_kit_component === true || item.item_name.includes(' - ');
    if (looks_like_kit_component) {
      const label = extractKitLabel(item.item_name) ?? 'Kit';
      const last = groups[groups.length - 1];
      if (last?.kit_label === label) {
        last.items.push(item);
        continue;
      }
      groups.push({ kit_label: label, items: [item] });
      continue;
    }
    groups.push({ kit_label: null, items: [item] });
  }
  return groups;
}

export function applyApolloErrorMessage(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
  set_error: (msg: string | null) => void,
): void {
  if (err instanceof ApolloError) {
    const code = err.graphQLErrors[0]?.message ?? err.message;
    if (code.startsWith('insufficient_stock')) {
      set_error(t('errors.insufficient_stock'));
      return;
    }
    if (code === 'payments_underpaid') {
      set_error(t('cashier_pos.error_underpaid'));
      return;
    }
    if (code === 'payments_overpaid') {
      set_error(t('cashier_pos.error_overpaid'));
      return;
    }
    if (code.startsWith('payment_reference_required')) {
      set_error(t('cashier_pos.error_reference_required'));
      return;
    }
    if (code.startsWith('order_not_chargeable')) {
      set_error(t('cashier_pos.error_not_chargeable'));
      return;
    }
    if (code.startsWith('order_not_discountable')) {
      set_error(t('cashier_pos.error_not_discountable'));
      return;
    }
    if (code.startsWith('order_not_discardable')) {
      set_error(t('cashier_pos.error_not_discardable'));
      return;
    }
    if (code.startsWith('order_not_cashier_editable')) {
      set_error(t('cashier_pos.error_not_discardable'));
      return;
    }
    if (code === 'order_requires_at_least_one_item_for_doctor_send') {
      set_error(t('doctor_pos.lines_required'));
      return;
    }
    if (code === 'customer_required') {
      set_error(t('doctor_pos.customer_required'));
      return;
    }
    if (code === 'pet_required') {
      set_error(t('doctor_pos.pet_required'));
      return;
    }
    if (code === 'current_weight_required' || code.startsWith('current_weight_required:')) {
      set_error(t('doctor_pos.weight_missing'));
      return;
    }
    if (code === 'consulting_room_required') {
      set_error(t('cashier_pos.select_consulting_room'));
      return;
    }
    if (code === 'reassign_reason_required') {
      set_error(t('cashier_pos.reassign_reason_required'));
      return;
    }
    if (code === 'consulting_room_not_found') {
      set_error(t('cashier_pos.error_consulting_room_not_found'));
      return;
    }
    if (code === 'consulting_room_inactive') {
      set_error(t('cashier_pos.error_consulting_room_inactive'));
      return;
    }
    if (code === 'consulting_room_branch_mismatch') {
      set_error(t('cashier_pos.error_consulting_room_branch_mismatch'));
      return;
    }
    if (code.startsWith('order_not_reassignable')) {
      set_error(t('cashier_pos.error_order_not_reassignable'));
      return;
    }
    if (code === 'consulting_room_same_as_current') {
      set_error(t('cashier_pos.error_consulting_room_same_as_current'));
      return;
    }
    if (code === 'current_weight_must_be_positive_int') {
      set_error(t('doctor_pos.weight_invalid'));
      return;
    }
    if (code === 'discount_must_be_non_negative') {
      set_error(t('cashier_pos.error_discount_negative'));
      return;
    }
    if (code.startsWith('discount_exceeds_max_percentage')) {
      set_error(t('cashier_pos.error_discount_max'));
      return;
    }
    if (code === 'workstation_branch_mismatch') {
      set_error(t('cashier_pos.error_branch_mismatch'));
      return;
    }
    if (code === 'cash_shift_required') {
      set_error(t('cashier_pos.error_shift_required'));
      return;
    }
    if (code === 'cash_shift_open_assigned_to_other_cashier') {
      set_error(t('cashier_pos.error_shift_assigned_to_other_cashier'));
      return;
    }
    if (code === 'open_account_requires_customer_and_pet') {
      set_error(t('cashier_pos.error_open_account_needs_customer_pet'));
      return;
    }
    if (code === 'open_account_customer_pet_mismatch') {
      set_error(t('cashier_pos.error_open_account_mismatch'));
      return;
    }
    if (code === 'open_account_kind_mismatch') {
      set_error(t('cashier_pos.error_open_account_kind_mismatch'));
      return;
    }
    if (code.startsWith('open_account_hospitalization_min_payment')) {
      set_error(t('cashier_pos.error_open_account_hospitalization_min_payment'));
      return;
    }
    if (code.startsWith('open_account_not_open')) {
      set_error(t('cashier_pos.error_open_account_not_open'));
      return;
    }
    if (code === 'idempotency_key_payload_mismatch' || code === 'idempotency_in_flight') {
      set_error(t('cashier_pos.error_idempotency_conflict'));
      return;
    }
    set_error(code);
    return;
  }
  if (err instanceof Error) {
    if (err.message === 'weight_invalid') {
      set_error(t('doctor_pos.weight_invalid'));
      return;
    }
    set_error(err.message);
  }
}
